import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Play, FileCode, FileText, Download, Loader2, CheckCircle, XCircle,
  Terminal, Wrench, Save, RefreshCw, RotateCcw, Edit3, Copy, Check,
  Camera, Eye, AlertTriangle
} from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { toast } from "sonner";
import Comments from "@/components/Comments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDuration(start, end) {
  const secs = Math.round((new Date(end) - new Date(start)) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function ContextualNextStep({ execution, onRerun, onNavigate, rerunning }) {
  const failureText = (
    execution.failure_reason ||
    execution.error_message ||
    (execution.logs?.find(l => l.level === 'error')?.message) ||
    ''
  ).toLowerCase();

  let title, description, primaryLabel, PrimaryIcon, primaryAction, secondaryLabel, secondaryAction;

  if (failureText.includes('element') || failureText.includes('selector') || failureText.includes('not found') || failureText.includes('locator')) {
    title = 'Element not found';
    description = 'A selector failed to locate a UI element. The page may have changed, or the selector is stale.';
    primaryLabel = 'Enable Self-Healing'; PrimaryIcon = Wrench;
    primaryAction = () => onNavigate(`/edit-test/${execution.test_case_id}`);
    secondaryLabel = 'Edit Test Steps';
    secondaryAction = () => onNavigate(`/edit-test/${execution.test_case_id}`);
  } else if (failureText.includes('timeout') || failureText.includes('timed out')) {
    title = 'Timeout';
    description = 'The test exceeded the time limit. The URL may be slow, unreachable, or the page takes too long to load.';
    primaryLabel = 'Re-run Test'; PrimaryIcon = RotateCcw;
    primaryAction = () => onRerun(true);
    secondaryLabel = 'Edit Steps & URL';
    secondaryAction = () => onNavigate(`/edit-test/${execution.test_case_id}`);
  } else if (failureText.includes('expected') || failureText.includes('assertion') || failureText.includes('mismatch')) {
    title = 'Assertion failed';
    description = 'A step\'s expected result didn\'t match what was found on the page.';
    primaryLabel = 'Update Expected Results'; PrimaryIcon = Edit3;
    primaryAction = () => onNavigate(`/edit-test/${execution.test_case_id}`);
    secondaryLabel = 'Regenerate & Run';
    secondaryAction = () => onRerun(false);
  } else {
    title = 'Test failed';
    description = 'Check the logs tab below for the specific error, then edit your test steps or let AI regenerate the code.';
    primaryLabel = 'Regenerate & Run'; PrimaryIcon = RefreshCw;
    primaryAction = () => onRerun(false);
    secondaryLabel = 'Edit Test Steps';
    secondaryAction = () => onNavigate(`/edit-test/${execution.test_case_id}`);
  }

  return (
    <div className="glass-card rounded-xl p-4 border-l-4 border-orange-400 bg-orange-50">
      <p className="text-sm font-bold text-orange-700 mb-0.5 flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4" />
        Suggested next step: {title}
      </p>
      <p className="text-sm text-slate-600 mb-3">{description}</p>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-600 rounded-lg" onClick={primaryAction} disabled={rerunning}>
          <PrimaryIcon className="w-3.5 h-3.5 mr-1.5" />
          {primaryLabel}
        </Button>
        <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-white rounded-lg" onClick={secondaryAction}>
          {secondaryLabel}
        </Button>
      </div>
    </div>
  );
}

export default function ExecutionDetails() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const initialFetchTsRef = useRef(Date.now());
  const hasShownFetchErrorRef = useRef(false);
  const [initializing, setInitializing] = useState(true);
  
  // Visual regression state (Phase C)
  const [baseline, setBaseline] = useState(null);       // baseline info from API
  const [diffResults, setDiffResults] = useState(null); // comparison results
  const [settingBaseline, setSettingBaseline] = useState(false);
  const [comparingDiff, setComparingDiff] = useState(false);
  const [showDiff, setShowDiff] = useState(false);      // toggle diff view in screenshots

  // Code editing state
  const [editMode, setEditMode] = useState({ javascript: false, python: false, java: false });
  const [editedCode, setEditedCode] = useState({ javascript: '', python: '', java: '' });
  const [codeCopied, setCodeCopied] = useState({ javascript: false, python: false, java: false });

  useEffect(() => {
    fetchExecution();
    const interval = setInterval(() => {
      if (polling) {
        fetchExecution();
      }
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, polling]);

  const fetchExecution = async () => {
    let keepLoading = false;
    try {
      const response = await axios.get(`${API}/executions/${executionId}`, { withCredentials: true });
      setExecution(response.data);
      setInitializing(false);
      hasShownFetchErrorRef.current = false;
      
      // Initialize edited code from generated code
      if (response.data.generated_code) {
        setEditedCode(prev => ({
          javascript: prev.javascript || response.data.generated_code.javascript || '',
          python: prev.python || response.data.generated_code.python || '',
          java: prev.java || response.data.generated_code.java || ''
        }));
      }
      
      // Fetch test case details to check for saved code
      if (response.data.test_case_id) {
        try {
          const testCaseRes = await axios.get(`${API}/test-cases/${response.data.test_case_id}`, { withCredentials: true });
          setTestCase(testCaseRes.data);
          fetchBaseline(testCaseRes.data.id);
        } catch (err) {
          console.error('Failed to fetch test case:', err);
        }
      }
      
      // Stop polling if execution is complete
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        setPolling(false);
      }
    } catch (error) {
      const status = error?.response?.status;
      const elapsedMs = Date.now() - initialFetchTsRef.current;
      const isTransientStartupError = (status === 404 || status === 502 || status === 503 || status === 504) && elapsedMs < 30000;

      // Fresh executions can briefly return 404/5xx while the backend/edge catches up.
      // Keep polling without spamming toasts or rendering "not found" too early.
      if (isTransientStartupError && !execution) {
        keepLoading = true;
        setInitializing(true);
        return;
      }

      console.error('Failed to fetch execution:', error);
      if (!hasShownFetchErrorRef.current) {
        toast.error(status === 404 ? 'Execution not found' : 'Failed to load execution details');
        hasShownFetchErrorRef.current = true;
      }
      setPolling(false);
      setInitializing(false);
    } finally {
      if (!keepLoading) {
        setLoading(false);
      }
    }
  };

  const generateReport = async (format) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      const response = await axios.post(`${API}/reports/generate?execution_id=${executionId}&format=${format}`, {}, { withCredentials: true });
      
      if (format === 'json') {
        const blob = new Blob([response.data.content], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `test-report-${executionId}.json`;
        a.click();
      } else if (format === 'html') {
        const blob = new Blob([response.data.content], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
      
      toast.success('Report generated!');
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    }
  };

  const downloadCode = (language) => {
    const code = editedCode[language] || execution?.generated_code?.[language];
    if (!code) return;
    
    const extensions = { javascript: 'js', python: 'py', java: 'java' };
    const extension = extensions[language];
    const blob = new Blob([code], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-${executionId}.${extension}`;
    a.click();
    toast.success(`${language} code downloaded!`);
  };

  const copyCode = async (language) => {
    const code = editedCode[language] || execution?.generated_code?.[language];
    if (!code) return;
    
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(prev => ({ ...prev, [language]: true }));
      toast.success(`${language} code copied to clipboard!`);
      setTimeout(() => {
        setCodeCopied(prev => ({ ...prev, [language]: false }));
      }, 2000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const toggleEditMode = (language) => {
    if (!editMode[language]) {
      // Entering edit mode - initialize with current code
      setEditedCode(prev => ({
        ...prev,
        [language]: prev[language] || execution?.generated_code?.[language] || ''
      }));
    }
    setEditMode(prev => ({ ...prev, [language]: !prev[language] }));
  };

  const saveCode = async (language) => {
    if (!execution?.test_case_id) {
      toast.error('Cannot save code - test case not found');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/test-cases/${execution.test_case_id}/save-code`, {
        saved_code: { [language]: editedCode[language] }
      }, { withCredentials: true });
      toast.success(`${language} code saved to test case!`);
      setEditMode(prev => ({ ...prev, [language]: false }));
      // Refresh test case to get updated saved code
      const testCaseRes = await axios.get(`${API}/test-cases/${execution.test_case_id}`, { withCredentials: true });
      setTestCase(testCaseRes.data);
    } catch (error) {
      console.error('Failed to save code:', error);
      toast.error('Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  // ── Visual Regression helpers (Phase C) ──────────────────────────────
  const fetchBaseline = async (testCaseId) => {
    try {
      const res = await axios.get(`${API}/visual-regression/baseline/${testCaseId}`, { withCredentials: true });
      setBaseline(res.data);
    } catch {
      setBaseline(null);
    }
  };

  const setAsBaseline = async () => {
    if (!execution?.test_case_id) return;
    setSettingBaseline(true);
    try {
      await axios.post(`${API}/visual-regression/baseline`, {
        test_case_id: execution.test_case_id,
        execution_id: execution.id,
      }, { withCredentials: true });
      toast.success('Baseline set! Future runs will be compared to this execution.');
      fetchBaseline(execution.test_case_id);
    } catch {
      toast.error('Failed to set baseline');
    } finally {
      setSettingBaseline(false);
    }
  };

  const compareToBaseline = async () => {
    if (!execution?.test_case_id) return;
    setComparingDiff(true);
    try {
      const res = await axios.post(`${API}/visual-regression/compare`, {
        test_case_id: execution.test_case_id,
        execution_id: execution.id,
      }, { withCredentials: true });
      setDiffResults(res.data);
      setShowDiff(true);
      const anyRegression = res.data.summary?.any_visual_regression;
      if (anyRegression) {
        toast.warning(`Visual regression detected! Avg diff: ${(res.data.summary.avg_diff_ratio * 100).toFixed(2)}%`);
      } else {
        toast.success('No visual regressions detected ✅');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Comparison failed';
      toast.error(msg);
    } finally {
      setComparingDiff(false);
    }
  };

  const rerunTest = async (useSavedCode) => {
    if (!execution?.test_case_id) {
      toast.error('Cannot rerun - test case not found');
      return;
    }
    
    setRerunning(true);
    try {
      const response = await axios.post(
        `${API}/executions/${executionId}/rerun?use_saved_code=${useSavedCode}`,
        {},
        { withCredentials: true }
      );
      toast.success(useSavedCode ? 'Rerun started!' : 'Regenerating code & running…');
      navigate(`/execution/${response.data.id}`);
    } catch (error) {
      console.error('Failed to rerun test:', error);
      toast.error('Failed to rerun test');
    } finally {
      setRerunning(false);
    }
  };

  const getStatusColor = (status, result) => {
    if (status === 'completed' && result === 'passed') return 'bg-green-50 text-green-700 border-green-200';
    if (status === 'completed' && result === 'failed') return 'bg-red-50 text-red-700 border-red-200';
    if (status === 'completed') return 'bg-green-50 text-green-700 border-green-200';
    if (status === 'running') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'success': return 'log-success';
      case 'error': return 'log-error';
      case 'warning': return 'log-warning';
      default: return 'log-info';
    }
  };

  const hasSavedCode = testCase?.saved_code && Object.keys(testCase.saved_code).length > 0;

  // Render code tab content
  const renderCodeTab = (language, displayName) => {
    const code = editMode[language] ? editedCode[language] : (execution?.generated_code?.[language] || 'Generating code...');
    const savedCodeExists = testCase?.saved_code?.[language];
    
    return (
      <TabsContent value={language}>
        <div className="space-y-3">
          {/* Code Actions Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {savedCodeExists && !editMode[language] && (
                <Badge className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20">
                  <Save className="w-3 h-3 mr-1" />
                  Saved code available
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid={`copy-${language}-btn`}
                size="sm"
                variant="ghost"
                className="text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
                onClick={() => copyCode(language)}
              >
                {codeCopied[language] ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
              <Button
                data-testid={`edit-${language}-btn`}
                size="sm"
                variant={editMode[language] ? "default" : "ghost"}
                className={editMode[language] 
                  ? "bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm" 
                  : "text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
                }
                onClick={() => toggleEditMode(language)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editMode[language] ? 'Cancel' : 'Edit'}
              </Button>
            </div>
          </div>
          
          {/* Code Editor/Viewer */}
          {editMode[language] ? (
            <Textarea
              data-testid={`code-editor-${language}`}
              className="h-[400px] w-full rounded-sm border border-white/10 bg-black/50 p-4 code-font text-sm text-slate-300 resize-none"
              value={editedCode[language]}
              onChange={(e) => setEditedCode(prev => ({ ...prev, [language]: e.target.value }))}
              placeholder={`Enter your ${displayName} code here...`}
            />
          ) : (
            <ScrollArea className="h-[400px] w-full rounded-sm border border-white/10 bg-black/50 p-4">
              <pre className="code-font text-sm text-slate-300 whitespace-pre-wrap">
                {code}
              </pre>
            </ScrollArea>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            {editMode[language] ? (
              <Button 
                data-testid={`save-${language}-btn`}
                size="sm"
                className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-lg"
                onClick={() => saveCode(language)}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save to Test Case
              </Button>
            ) : (
              <Button 
                data-testid={`download-${language}-btn`}
                size="sm"
                className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-lg"
                onClick={() => downloadCode(language)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download {displayName}
              </Button>
            )}
          </div>
        </div>
      </TabsContent>
    );
  };

  if (loading || initializing) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] text-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-12 h-12 text-[#00E599] animate-spin" />
          <p className="text-slate-400 text-sm">Loading execution details...</p>
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] text-slate-900 flex items-center justify-center">
        <p>Execution not found</p>
      </div>
    );
  }

  const isFailed = execution.result === 'failed' || (execution.status === 'completed' && execution.result === 'failed');
  const isPassed = execution.result === 'passed' || (execution.status === 'completed' && execution.result === 'passed');

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="execution-details-page">
      <AppNav />

      {/* Main Content */}
      <div className="px-6 py-8 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-slate-400 mb-3">
              <button onClick={() => navigate('/dashboard')} className="hover:text-slate-700 transition-colors">Dashboard</button>
              <span>/</span>
              <span className="text-slate-700 font-medium truncate max-w-xs">{testCase?.name || 'Execution'}</span>
            </div>

            {/* Test name as primary heading */}
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              {testCase?.name || 'Test Execution'}
            </h1>
            <p className="text-xs text-slate-400 font-mono mb-5">
              Execution ID: {execution.id}
            </p>

            {/* Hero status banner */}
            {isPassed && (
              <div className="flex items-center gap-4 p-5 rounded-xl bg-green-50 border border-green-200 mb-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-bold text-green-700">All steps passed</p>
                  <p className="text-sm text-green-600">
                    {execution.end_time ? `Completed in ${formatDuration(execution.start_time, execution.end_time)}` : 'Completed successfully'}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-green-600 text-white hover:bg-green-700 rounded-lg flex-shrink-0"
                  onClick={() => rerunTest(true)}
                  disabled={rerunning}
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Run Again
                </Button>
              </div>
            )}

            {isFailed && (
              <div className="space-y-3 mb-3">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-red-50 border border-red-200">
                  <div className="w-12 h-12 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-7 h-7 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold text-red-700">Test failed</p>
                    {(execution.failure_reason || execution.error_message) && (
                      <p className="text-sm text-red-600 mt-0.5 font-mono truncate">
                        {execution.failure_reason || execution.error_message}
                      </p>
                    )}
                    {execution.end_time && (
                      <p className="text-xs text-red-400 mt-1">Duration: {formatDuration(execution.start_time, execution.end_time)}</p>
                    )}
                  </div>
                </div>
                <ContextualNextStep
                  execution={execution}
                  onRerun={rerunTest}
                  onNavigate={navigate}
                  rerunning={rerunning}
                />
              </div>
            )}

            {execution.status === 'running' && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 mb-3">
                <Loader2 className="w-6 h-6 text-amber-600 animate-spin flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">Test is running...</p>
                  <p className="text-xs text-amber-600">Refreshing every 3 seconds</p>
                </div>
              </div>
            )}
          </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Generated Code */}
                <Card className="glass-card rounded-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      <FileCode className="w-5 h-5 mr-2 text-[#00E599]" />
                      Generated Test Code
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="javascript" className="w-full">
                      <TabsList className="bg-slate-100 mb-4">
                        <TabsTrigger value="javascript" data-testid="code-tab-js" className="data-[state=active]:bg-[#00b37a] data-[state=active]:text-white">
                          JavaScript
                        </TabsTrigger>
                        <TabsTrigger value="python" data-testid="code-tab-py" className="data-[state=active]:bg-[#00b37a] data-[state=active]:text-white">
                          Python
                        </TabsTrigger>
                        <TabsTrigger value="java" data-testid="code-tab-java" className="data-[state=active]:bg-[#00b37a] data-[state=active]:text-white">
                          Java
                        </TabsTrigger>
                      </TabsList>

                      {renderCodeTab('javascript', 'JavaScript')}
                      {renderCodeTab('python', 'Python')}
                      {renderCodeTab('java', 'Java')}
                    </Tabs>
                  </CardContent>
                </Card>

                {/* Execution Logs */}
                <Card className="glass-card rounded-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      <FileText className="w-5 h-5 mr-2 text-[#06B6D4]" />
                      Logs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="standard" className="w-full">
                      <TabsList className="bg-slate-100 mb-4">
                        <TabsTrigger value="standard" data-testid="logs-tab-standard" className="data-[state=active]:bg-[#00b37a] data-[state=active]:text-white">
                          Standard Logs
                        </TabsTrigger>
                        <TabsTrigger value="raw" data-testid="logs-tab-raw" className="data-[state=active]:bg-[#00b37a] data-[state=active]:text-white">
                          <Terminal className="w-4 h-4 mr-2" />
                          Raw Logs
                        </TabsTrigger>
                        {execution.self_healing_actions && execution.self_healing_actions.length > 0 && (
                          <TabsTrigger value="healing" data-testid="logs-tab-healing" className="data-[state=active]:bg-[#D946EF] data-[state=active]:text-white">
                            <Wrench className="w-4 h-4 mr-2" />
                            Self-Healing ({execution.self_healing_actions.length})
                          </TabsTrigger>
                        )}
                      </TabsList>

                      <TabsContent value="standard">
                        <ScrollArea className="h-[400px] w-full rounded-sm border border-white/10 bg-black/50 p-4">
                          {execution.logs && execution.logs.length > 0 ? (
                            <div className="code-font text-sm space-y-2">
                              {execution.logs.map((log, idx) => (
                                <div key={idx} className={`flex items-start gap-2 flex-wrap ${getLogColor(log.level)}`} data-testid={`log-entry-${idx}`}>
                                  <span className="text-slate-500 flex-shrink-0">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                  <span className="font-bold flex-shrink-0">{log.level.toUpperCase()}:</span>
                                  <span className="flex-1">{log.message}</span>
                                  {/* Phase A: Auto-healed badge */}
                                  {(log.healed || (log.message && log.message.toLowerCase().includes('healed'))) && (
                                    <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
                                      <Wrench className="w-3 h-3" /> Auto-healed
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-8">No logs available yet...</p>
                          )}
                        </ScrollArea>
                      </TabsContent>

                      <TabsContent value="raw">
                        <ScrollArea className="h-[400px] w-full rounded-sm border border-white/10 bg-black/50 p-4">
                          {execution.raw_logs && execution.raw_logs.length > 0 ? (
                            <div className="code-font text-xs space-y-1">
                              {execution.raw_logs.map((log, idx) => (
                                <div key={idx} className={getLogColor(log.level)} data-testid={`raw-log-entry-${idx}`}>
                                  <div className="flex items-start gap-2">
                                    <span className="text-slate-600 flex-shrink-0">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                    <span className="font-bold text-[#00E599] flex-shrink-0">[{log.level.toUpperCase()}]</span>
                                    <span className="flex-1">{log.message}</span>
                                  </div>
                                  {log.data && (
                                    <pre className="ml-4 mt-1 text-slate-500 text-xs">
                                      {JSON.stringify(log.data, null, 2)}
                                    </pre>
                                  )}
                                  {log.stack && (
                                    <pre className="ml-4 mt-1 text-red-400 text-xs overflow-auto">
                                      {log.stack}
                                    </pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-8">No raw logs available...</p>
                          )}
                        </ScrollArea>
                      </TabsContent>

                      {execution.self_healing_actions && execution.self_healing_actions.length > 0 && (
                        <TabsContent value="healing">
                          <ScrollArea className="h-[400px] w-full rounded-sm border border-[#D946EF]/20 bg-[#D946EF]/5 p-4">
                            <div className="space-y-4">
                              {execution.self_healing_actions.map((action, idx) => {
                                const isRealHeal = action.action === 'ai_self_heal';
                                return (
                                  <div key={idx} className="glass-card p-4 rounded-sm border border-[#D946EF]/20">
                                    <div className="flex items-start gap-3">
                                      <div className="w-8 h-8 bg-[#D946EF]/10 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Wrench className="w-4 h-4 text-[#D946EF]" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        {/* Header row */}
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                          <p className="text-sm font-semibold text-[#D946EF]">
                                            {action.step_index != null ? `Step ${action.step_index}` : (action.step ? `Step ${action.step}` : 'Execution')}
                                          </p>
                                          {isRealHeal ? (
                                            <span className="text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded">
                                              🔧 AI Self-Healed
                                            </span>
                                          ) : (
                                            <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                                              {action.action.replace(/_/g, ' ')}
                                            </span>
                                          )}
                                          {action.confidence != null && (
                                            <span className="text-xs text-green-400 font-mono">
                                              {Math.round(action.confidence * 100)}% confidence
                                            </span>
                                          )}
                                        </div>

                                        {/* Step description */}
                                        {action.step_description && (
                                          <p className="text-xs text-slate-300 mb-2 italic">"{action.step_description}"</p>
                                        )}

                                        {/* Selector diff — only for real heals */}
                                        {isRealHeal && (
                                          <div className="bg-black/40 rounded p-2 mb-2 space-y-1 font-mono text-xs">
                                            {action.original_selectors && action.original_selectors.length > 0 && (
                                              <div>
                                                <span className="text-red-400">✗ Failed:  </span>
                                                <span className="text-slate-400">{action.original_selectors.join(', ')}</span>
                                              </div>
                                            )}
                                            {action.new_selector && (
                                              <div>
                                                <span className="text-green-400">✓ Healed:  </span>
                                                <span className="text-green-300">{action.new_selector}</span>
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* Reason & timestamp */}
                                        {action.reason && (
                                          <p className="text-xs text-slate-400 mb-1">{action.reason}</p>
                                        )}
                                        <p className="text-xs text-slate-600">
                                          {new Date(action.healed_at || action.timestamp).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        </TabsContent>
                      )}
                    </Tabs>
                  </CardContent>
                </Card>

                {/* Screenshots */}
                {execution.screenshots && execution.screenshots.length > 0 && (
                  <Card className="glass-card rounded-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        <span>📸 Screenshots ({execution.screenshots.length})</span>
                        {diffResults && (
                          <button
                            className={`text-xs px-3 py-1 rounded-lg border transition-colors ${showDiff ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-800'}`}
                            onClick={() => setShowDiff(v => !v)}
                          >
                            <Eye className="w-3 h-3 inline mr-1" />
                            {showDiff ? 'Diff ON' : 'Diff OFF'}
                          </button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {execution.screenshots.map((screenshot, idx) => {
                          // Handle both storage_url and legacy base64 screenshot
                          const imgSrc = screenshot.storage_url
                            ? `${BACKEND_URL}${screenshot.storage_url.startsWith('/') ? '' : '/api/storage/'}${screenshot.storage_url}`
                            : screenshot.screenshot;

                          // Find matching diff result for this step
                          const stepKey = screenshot.step;
                          const diffStep = showDiff && diffResults?.steps?.find(s => s.step === stepKey);
                          const hasDiff = diffStep && diffStep.ok && diffStep.diff_image_b64;
                          const diffPct = diffStep ? ((diffStep.diff_ratio || 0) * 100).toFixed(2) : null;
                          const isRegression = diffStep && (diffStep.diff_ratio || 0) > 0.01;

                          return (
                            <div key={idx} className="glass-card p-4 rounded-sm" data-testid={`screenshot-${idx}`}>
                              <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <Badge className="bg-[#00E599]/10 text-[#00E599] border-[#00E599]/20">
                                  Step {screenshot.step === 0 ? 'Initial' : screenshot.step}
                                </Badge>
                                <p className="text-sm text-slate-600">{screenshot.description}</p>
                                {diffStep && (
                                  <Badge className={isRegression
                                    ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                    : 'bg-[#00E599]/10 text-[#00E599] border-[#00E599]/20'
                                  }>
                                    {isRegression ? (
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                    ) : (
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                    )}
                                    {diffPct}% diff
                                  </Badge>
                                )}
                              </div>

                              {hasDiff ? (
                                /* Side-by-side: current (left) + diff overlay (right) */
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-xs text-slate-500 mb-1 text-center">Current</p>
                                    <div className="bg-black/50 border border-white/10 rounded-sm overflow-hidden">
                                      {imgSrc ? (
                                        <img
                                          src={imgSrc}
                                          alt="Current screenshot"
                                          className="w-full h-auto"
                                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                                        />
                                      ) : (
                                        <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
                                          Unavailable
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500 mb-1 text-center">
                                      Diff overlay
                                      {isRegression && <span className="text-red-400 ml-1">(regression)</span>}
                                    </p>
                                    <div className="bg-black/50 border border-red-500/20 rounded-sm overflow-hidden">
                                      <img
                                        src={`data:image/png;base64,${diffStep.diff_image_b64}`}
                                        alt="Diff overlay"
                                        className="w-full h-auto"
                                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* Normal single screenshot */
                                <div className="relative bg-black/50 border border-white/10 rounded-sm overflow-hidden">
                                  {imgSrc ? (
                                    <img
                                      src={imgSrc}
                                      alt={`Screenshot for ${screenshot.description}`}
                                      className="w-full h-auto"
                                      style={{ maxHeight: '500px', objectFit: 'contain' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div className="hidden items-center justify-center h-32 text-slate-500 text-sm">
                                    Screenshot unavailable
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Rerun Options */}
                <Card className="glass-card rounded-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      <RotateCcw className="w-5 h-5 mr-2 text-[#00E599]" />
                      Rerun Test
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Rerun — runs the test again as-is (no code regeneration). Always available. */}
                    <Button
                      data-testid="rerun-btn"
                      className="w-full bg-[#00b37a] text-white hover:bg-[#009966] rounded-lg"
                      onClick={() => rerunTest(true)}
                      disabled={rerunning || execution.status === 'running'}
                    >
                      {rerunning ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-2" />
                      )}
                      Rerun
                    </Button>
                    <p className="text-xs text-slate-500 text-center -mt-1">
                      Runs the same test again{hasSavedCode ? ' using the saved code' : ''}.
                    </p>

                    {/* Regenerate & Run — rebuilds the test code from the steps, then runs. */}
                    <Button
                      data-testid="rerun-regenerate-btn"
                      variant="outline"
                      className="w-full bg-transparent border border-[#D946EF]/40 text-[#D946EF] hover:bg-[#D946EF]/10 rounded-sm"
                      onClick={() => rerunTest(false)}
                      disabled={rerunning || execution.status === 'running'}
                    >
                      {rerunning ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Regenerate &amp; Run
                    </Button>
                    <p className="text-xs text-slate-500 text-center -mt-1">
                      Rebuilds the whole test code with AI, then runs it.
                    </p>
                  </CardContent>
                </Card>

                {/* Execution Info */}
                <Card className="glass-card rounded-xl">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Execution Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div>
                      <p className="text-slate-500 mb-1">Status</p>
                      <Badge className={getStatusColor(execution.status, execution.result)}>{execution.status}</Badge>
                    </div>
                    <div>
                      <p className="text-slate-500 mb-1">Result</p>
                      <p className="text-slate-900 font-medium">{execution.result || 'Pending'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 mb-1">Started</p>
                      <p className="text-slate-700">{new Date(execution.start_time).toLocaleString()}</p>
                    </div>
                    {execution.end_time && (
                      <div>
                        <p className="text-slate-500 mb-1">Completed</p>
                        <p className="text-slate-700">{new Date(execution.end_time).toLocaleString()}</p>
                      </div>
                    )}
                    {testCase && (
                      <div>
                        <p className="text-slate-500 mb-1">Test Case</p>
                        <p className="text-slate-900 font-medium truncate">{testCase.name}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Generate Reports */}
                <Card className="glass-card rounded-xl">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Generate Report</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      data-testid="generate-html-btn"
                      className="w-full bg-[#00b37a] text-white hover:bg-[#009966] rounded-lg"
                      onClick={() => generateReport('html')}
                      disabled={execution.status === 'pending' || execution.status === 'running'}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      HTML Report
                    </Button>
                    <Button 
                      data-testid="generate-json-btn"
                      className="w-full bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm"
                      onClick={() => generateReport('json')}
                      disabled={execution.status === 'pending' || execution.status === 'running'}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      JSON Report
                    </Button>
                  </CardContent>
                </Card>

                {/* Visual Regression Card */}
                {execution.screenshots && execution.screenshots.length > 0 && (
                  <Card className="glass-card rounded-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        <Camera className="w-5 h-5 mr-2 text-[#06B6D4]" />
                        Visual Regression
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Baseline status */}
                      {baseline && baseline.step_count > 0 ? (
                        <div className="glass-card p-3 rounded-sm border border-[#00E599]/20">
                          <p className="text-xs text-[#00E599] font-semibold mb-1">✓ Baseline set</p>
                          <p className="text-xs text-slate-400">{baseline.step_count} step{baseline.step_count !== 1 ? 's' : ''} saved</p>
                        </div>
                      ) : (
                        <div className="glass-card p-3 rounded-sm border border-slate-700">
                          <p className="text-xs text-slate-400">No baseline set yet. Run the test once and set it as baseline.</p>
                        </div>
                      )}

                      {/* Set as Baseline button */}
                      <Button
                        data-testid="set-baseline-btn"
                        className="w-full bg-[#06B6D4] text-black hover:bg-[#0891B2] rounded-sm"
                        onClick={setAsBaseline}
                        disabled={settingBaseline || execution.status !== 'completed'}
                      >
                        {settingBaseline ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Camera className="w-4 h-4 mr-2" />
                        )}
                        Set as Baseline
                      </Button>

                      {/* Compare button — only shown if baseline exists */}
                      {baseline && baseline.step_count > 0 && (
                        <Button
                          data-testid="compare-baseline-btn"
                          className="w-full bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm"
                          onClick={compareToBaseline}
                          disabled={comparingDiff || execution.status !== 'completed'}
                        >
                          {comparingDiff ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4 mr-2" />
                          )}
                          Compare to Baseline
                        </Button>
                      )}

                      {/* Diff summary */}
                      {diffResults && (
                        <div className={`glass-card p-3 rounded-sm border ${diffResults.summary?.any_visual_regression ? 'border-red-500/30 bg-red-500/5' : 'border-[#00E599]/20 bg-[#00E599]/5'}`}>
                          <p className={`text-xs font-semibold mb-1 ${diffResults.summary?.any_visual_regression ? 'text-red-400' : 'text-[#00E599]'}`}>
                            {diffResults.summary?.any_visual_regression ? '⚠ Regression detected' : '✓ No regression'}
                          </p>
                          <p className="text-xs text-slate-400">
                            Avg diff: {((diffResults.summary?.avg_diff_ratio || 0) * 100).toFixed(2)}% across {diffResults.summary?.total_steps_compared || 0} steps
                          </p>
                          <button
                            className="mt-2 text-xs text-[#06B6D4] hover:underline"
                            onClick={() => setShowDiff(v => !v)}
                          >
                            {showDiff ? 'Hide diff overlay' : 'Show diff overlay'}
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Comments Section */}
                {execution?.test_case_id && (
                  <Card className="glass-card rounded-xl">
                    <CardContent className="pt-6">
                      <Comments entityType="execution" entityId={executionId} />
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}
